import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FolderOpen, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { UserHeader } from '../../components/UserHeader';
import { EstadoDocBadge, ListaArchivos, SubirArchivos, borrarConAviso, subirArchivos } from '../../components/actualizacion-familiar/DocumentacionAdmin';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api/actualizacion-familiar/mi-documentacion';

const TEXTOS = {
  pendiente_carga: 'Subí la documentación que acredite el vínculo con tus hijos/as (por ejemplo partida de nacimiento o DNI). Podés subir PDF o fotos.',
  en_revision: 'Recibimos tu documentación y la estamos revisando. Si te faltó algo, podés agregar más archivos.',
  aprobada: 'Tu documentación fue aprobada. ¡Gracias!',
  rechazada: 'Necesitamos que vuelvas a cargar la documentación.',
};

export default function DocumentacionFamiliarPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => {
    axios.get(BASE).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || 'No se pudo cargar.'));
  }, []);

  const setDoc = (documentacion) => setData((d) => ({ ...d, documentacion }));
  const subir = async (files) => {
    setSubiendo(true);
    try { setDoc(await subirArchivos(`${BASE}/archivos`, files)); toast.success('Documentación enviada'); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo subir'); } finally { setSubiendo(false); }
  };
  const borrar = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.nombre}"?`)) return;
    await borrarConAviso(async () => (await axios.delete(`${BASE}/archivos/${a.id}`)).data.documentacion, setDoc);
  };

  const doc = data?.documentacion;
  let contenido;
  if (error) contenido = <p className="text-red-600" data-testid="docf-error">{error}</p>;
  else if (!data) contenido = <Loader2 className="h-6 w-6 animate-spin text-gray-400" />;
  else if (!doc) contenido = <p className="text-gray-600" data-testid="docf-no-solicitada">Por ahora no tenés documentación pendiente de cargar.</p>;
  else contenido = (
    <div className="rounded-xl border bg-white p-6 space-y-5" data-testid="docf-panel">
      <div className="flex items-center gap-2"><span className="text-sm text-gray-500">Estado:</span><EstadoDocBadge estado={doc.estado} testid="docf-estado" /></div>
      <p className="text-sm text-gray-700">{TEXTOS[doc.estado]}</p>
      {doc.estado === 'rechazada' && doc.motivo_rechazo && (
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="docf-motivo"><b>Motivo:</b> {doc.motivo_rechazo}</p>
      )}
      <ListaArchivos archivos={doc.archivos || []} urlDe={(a) => `${BASE}/archivos/${a.id}`}
        puedeBorrar={(a) => a.subido_por === 'matriculado' && doc.estado !== 'aprobada'} onBorrar={borrar} />
      {doc.estado !== 'aprobada' && <SubirArchivos onFiles={subir} subiendo={subiendo} label="Subir documentación" testid="docf-subir" />}
      <p className="text-xs text-gray-400">PDF o imágenes, hasta 10 MB por archivo.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <UserHeader />
      <main className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-3"><FolderOpen className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Documentación · Programa Escolar 2027</h1>
            {data?.nombre && <p className="text-sm text-gray-600 mt-1">Hola {data.nombre}.</p>}
          </div>
        </div>
        {contenido}
      </main>
    </div>
  );
}
